import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { DropdownMenuCheckboxItem } from '@/components/atoms/dropdown-menu';
import { useTheme } from '@/hooks/use-theme';

/**
 * Dark-mode row for the header's avatar menu: a checkbox menu item (so assistive
 * tech announces it as a single toggle with its checked state) that flips
 * between light and dark. next-themes persists the choice and toggles the
 * `.dark` class on <html>, which the CSS tokens key off.
 *
 * The switch on the right is a purely visual, non-interactive indicator (plain
 * spans, `aria-hidden`) styled to match the real Switch — so the checkbox item
 * is the ONE interactive control, with no nested widget. `onSelect` is prevented
 * so toggling doesn't close the menu.
 */
export function ThemeToggleMenuItem() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isDark = mounted && resolvedTheme === 'dark';
  const state = isDark ? 'checked' : 'unchecked';

  return (
    <DropdownMenuCheckboxItem
      checked={isDark}
      onCheckedChange={(v) => setTheme(v ? 'dark' : 'light')}
      onSelect={(e) => e.preventDefault()}
      // Drop the default left checkmark (the visual switch on the right is the
      // indicator) and reclaim its padding.
      className="flex cursor-pointer items-center justify-between gap-2 pl-2 [&>span:first-child]:hidden"
    >
      <span className="flex items-center gap-2">
        {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        Dark mode
      </span>
      {/* Decorative switch — mirrors the Switch atom's look, but non-interactive. */}
      <span
        aria-hidden
        data-state={state}
        className="inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent shadow-xs transition-colors data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"
      >
        <span
          data-state={state}
          className="block size-4 rounded-full bg-background shadow-sm transition-transform data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0"
        />
      </span>
    </DropdownMenuCheckboxItem>
  );
}
